// Admin Operations Dashboard — single-screen home for admin actions.
// Shows: pending payment requests, withdrawals, deposits, marketplace orders by status.
// Auto-refreshes every 15s. Inline Approve/Reject buttons.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, CheckCircle, XCircle, Package, DollarSign, ArrowDownCircle, ArrowUpCircle, Bell, Volume2, VolumeX, Bike } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../utils/api';

const StatusPill = ({ s }) => {
  const map = {
    pending: 'bg-amber-500/25 text-amber-300 border-amber-400/60',
    confirmed: 'bg-cyan-500/25 text-cyan-300 border-cyan-400/60',
    packed: 'bg-indigo-500/25 text-indigo-300 border-indigo-400/60',
    shipped: 'bg-sky-500/25 text-sky-300 border-sky-400/60',
    preparing: 'bg-sky-500/25 text-sky-300 border-sky-400/60',
    out_for_delivery: 'bg-purple-500/25 text-purple-300 border-purple-400/60',
    delivered: 'bg-emerald-500/25 text-emerald-300 border-emerald-400/60',
    completed: 'bg-emerald-500/25 text-emerald-300 border-emerald-400/60',
    rejected: 'bg-rose-500/25 text-rose-300 border-rose-400/60',
  };
  return <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-black uppercase ${map[s] || 'bg-slate-600'}`}>{s || 'unknown'}</span>;
};

export default function AdminOpsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/admin/marketplace/ops-dashboard'); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  // Approve / reject actions — try common admin endpoints
  const actOnDeposit = async (id, action) => {
    try {
      await api.post(`/admin/avx/deposit-request/${id}/${action}`, {});
      toast.success(`Deposit ${action}d`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || `${action} failed`); }
  };
  const actOnWithdraw = async (id, action) => {
    try {
      // Try newer endpoint first, fall back to legacy
      await api.post(`/admin/at-withdraw/${id}/${action}`, {}).catch(async () => {
        await api.post(`/admin/withdrawals/${id}/${action}`, {});
      });
      toast.success(`Withdraw ${action}d`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || `${action} failed`); }
  };
  const actOnFundRequest = async (id, action) => {
    try { await api.post(`/admin/fund-requests/${id}/${action}`, {}); toast.success(`Fund request ${action}d`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || `${action} failed`); }
  };
  const updateOrderStatus = async (oid, status) => {
    try { await api.post(`/marketplace/order/${oid}/status`, { status, note: `Admin update → ${status}` });
          toast.success(`Order → ${status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!data) return (
    <div className="min-h-screen bg-slate-950 text-white p-6 flex items-center justify-center">
      <div>{loading ? '⏳ Loading…' : 'No data'}</div>
    </div>
  );

  const c = data.counts || {};
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 text-white p-4 sm:p-6" data-testid="admin-ops-dashboard">
      <Toaster position="top-right" richColors />
      {/* Header */}
      <div className="max-w-7xl mx-auto flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">👑 Admin Operations</h1>
          <div className="text-[10px] opacity-70">Last refresh: {(data.generated_at || '').slice(11, 19)} · Auto-refresh {autoRefresh ? 'ON (15s)' : 'OFF'}</div>
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={() => setAutoRefresh((v) => !v)} data-testid="ops-toggle-auto"
                  className={`px-3 py-1.5 rounded-lg text-xs font-black ${autoRefresh ? 'bg-emerald-500 text-slate-900' : 'bg-slate-700 text-white'}`}>
            {autoRefresh ? '⏱ Auto ON' : '⏱ Auto OFF'}
          </button>
          <button onClick={load} data-testid="ops-refresh"
                  className="px-3 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-black flex items-center gap-1">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard icon={<DollarSign size={18} />} label="Pending Payments" v={c.pending_payments + (c.initiated_payment_txns || 0)} tint="from-emerald-500 to-teal-600" testid="ops-stat-payments" />
        <StatCard icon={<ArrowUpCircle size={18} />} label="Pending Withdrawals" v={c.pending_withdrawals} tint="from-rose-500 to-pink-600" testid="ops-stat-withdrawals" />
        <StatCard icon={<ArrowDownCircle size={18} />} label="Pending Deposits" v={c.pending_deposits} tint="from-sky-500 to-blue-600" testid="ops-stat-deposits" />
        <StatCard icon={<Package size={18} />} label="Active Orders" v={(c.orders_by_status?.pending || 0) + (c.orders_by_status?.preparing || 0) + (c.orders_by_status?.out_for_delivery || 0) + (c.orders_by_status?.confirmed || 0)} tint="from-purple-500 to-pink-600" testid="ops-stat-orders" />
      </div>

      {/* Order breakdown by status */}
      <div className="max-w-7xl mx-auto rounded-2xl bg-slate-900/70 border border-slate-700 p-4 mb-5">
        <div className="text-xs font-black text-purple-300 uppercase mb-3">📦 Marketplace Orders — by Status</div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {['pending', 'confirmed', 'packed', 'shipped', 'out_for_delivery', 'delivered', 'rejected'].map((s) => (
            <div key={s} className="rounded-xl bg-slate-800/60 p-3 text-center" data-testid={`ops-status-${s}`}>
              <StatusPill s={s} />
              <div className="text-2xl font-black mt-1">{c.orders_by_status?.[s] || 0}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Sales heatmap by city — spans full width on mobile */}
        <div className="lg:col-span-2"><CityHeatmap /></div>

        {/* Unified Ledger — spans full width */}
        <div className="lg:col-span-2"><UnifiedLedger /></div>

        {/* Delivery Partners live list */}
        <div className="lg:col-span-2"><DeliveryPartnersPanel /></div>

        {/* Voice + Push subscribe (STEP 4) */}
        <div className="lg:col-span-2"><VoiceAlertsPanel counts={c} /></div>

        {/* Recent orders */}
        <Panel title="🛒 Recent Orders" testid="ops-orders-panel">
          {(data.recent_orders || []).slice(0, 12).map((o) => (
            <div key={o.id} className="rounded bg-slate-800/40 p-2 space-y-1 text-xs" data-testid={`ops-order-${o.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-black truncate">{o.buyer_name || '—'} · <span className="text-yellow-300">{o.total_at} AT</span></div>
                  <div className="text-[9px] opacity-70 truncate">{o.item_titles || `${o.item_count} items`} · {o.id.slice(0, 8)}</div>
                </div>
                <StatusPill s={o.status} />
              </div>
              <div className="flex gap-1 flex-wrap">
                {['confirmed', 'packed', 'shipped', 'out_for_delivery', 'delivered', 'rejected'].filter((s) => s !== o.status).map((s) => (
                  <button key={s} onClick={() => updateOrderStatus(o.id, s)} data-testid={`ops-order-${o.id}-${s}`}
                          className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700 hover:bg-slate-600 font-black uppercase">→ {s.replace('_', ' ')}</button>
                ))}
              </div>
            </div>
          ))}
          {(data.recent_orders || []).length === 0 && <EmptyLabel>No orders yet</EmptyLabel>}
        </Panel>

        {/* Pending deposits */}
        <Panel title="⬇️ Pending Deposit Requests" testid="ops-deposits-panel">
          {(data.pending_deposits || []).map((d) => (
            <div key={d.id} className="rounded bg-slate-800/40 p-2 text-xs space-y-1" data-testid={`ops-deposit-${d.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-black truncate">{d.user_name || d.user_username || '—'} · <span className="text-emerald-300">₹{d.amount}</span></div>
                  <div className="text-[9px] opacity-70">{d.payment_method || '—'} · {(d.created_at || '').slice(0, 16).replace('T', ' ')}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => actOnDeposit(d.id, 'approve')} data-testid={`ops-dep-approve-${d.id}`} className="p-1 rounded bg-emerald-500 text-slate-900"><CheckCircle size={12} /></button>
                  <button onClick={() => actOnDeposit(d.id, 'reject')} data-testid={`ops-dep-reject-${d.id}`} className="p-1 rounded bg-rose-600 text-white"><XCircle size={12} /></button>
                </div>
              </div>
            </div>
          ))}
          {(data.pending_deposits || []).length === 0 && <EmptyLabel>No pending deposits</EmptyLabel>}
        </Panel>

        {/* Pending withdrawals */}
        <Panel title="⬆️ Pending Withdrawals" testid="ops-withdraw-panel">
          {[...(data.pending_withdrawals || []), ...(data.pending_withdrawals_legacy || [])].map((w) => (
            <div key={w.id} className="rounded bg-slate-800/40 p-2 text-xs space-y-1" data-testid={`ops-withdraw-${w.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-black truncate">{w.username || w.user_name || '—'} · <span className="text-rose-300">{w.amount} AT</span></div>
                  <div className="text-[9px] opacity-70 truncate">{w.method || '—'} · {w.upi_id || w.bank_name || w.account_number || ''}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => actOnWithdraw(w.id, 'approve')} data-testid={`ops-with-approve-${w.id}`} className="p-1 rounded bg-emerald-500 text-slate-900"><CheckCircle size={12} /></button>
                  <button onClick={() => actOnWithdraw(w.id, 'reject')} data-testid={`ops-with-reject-${w.id}`} className="p-1 rounded bg-rose-600 text-white"><XCircle size={12} /></button>
                </div>
              </div>
            </div>
          ))}
          {[...(data.pending_withdrawals || []), ...(data.pending_withdrawals_legacy || [])].length === 0 && <EmptyLabel>No pending withdrawals</EmptyLabel>}
        </Panel>

        {/* Pending payments / fund requests */}
        <Panel title="💰 Pending Payments / Fund Requests" testid="ops-payments-panel">
          {(data.pending_payments || []).map((p) => (
            <div key={p.id} className="rounded bg-slate-800/40 p-2 text-xs space-y-1" data-testid={`ops-fund-${p.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-black truncate">{p.user_name || p.user_email || '—'} · <span className="text-emerald-300">₹{p.amount}</span></div>
                  <div className="text-[9px] opacity-70">{(p.created_at || '').slice(0, 16).replace('T', ' ')}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => actOnFundRequest(p.id, 'approve')} data-testid={`ops-fund-approve-${p.id}`} className="p-1 rounded bg-emerald-500 text-slate-900"><CheckCircle size={12} /></button>
                  <button onClick={() => actOnFundRequest(p.id, 'reject')} data-testid={`ops-fund-reject-${p.id}`} className="p-1 rounded bg-rose-600 text-white"><XCircle size={12} /></button>
                </div>
              </div>
            </div>
          ))}
          {(data.initiated_payment_txns || []).map((t) => (
            <div key={t.id} className="rounded bg-slate-800/40 p-2 text-xs" data-testid={`ops-pay-txn-${t.id}`}>
              <div className="text-[11px] font-black truncate">{t.user_id?.slice(0, 8)} · ₹{t.amount} <span className="text-amber-300">· initiated</span></div>
              <div className="text-[9px] opacity-70">{t.payment_method} · {(t.created_at || '').slice(0, 16).replace('T', ' ')}</div>
            </div>
          ))}
          {(data.pending_payments || []).length === 0 && (data.initiated_payment_txns || []).length === 0 && <EmptyLabel>No pending payments</EmptyLabel>}
        </Panel>
      </div>

      <div className="max-w-7xl mx-auto mt-6 text-center text-[10px] opacity-60">
        <Link to="/admin/anant" className="hover:text-purple-300">→ Full Admin Console</Link> · <Link to="/marketplace" className="hover:text-purple-300">→ Marketplace</Link>
      </div>
    </div>
  );
}

const StatCard = ({ icon, label, v, tint, testid }) => (
  <div data-testid={testid} className={`rounded-2xl bg-gradient-to-br ${tint} p-4 text-white shadow-lg`}>
    <div className="flex items-center justify-between mb-1">{icon}<Bell size={12} className="opacity-60" /></div>
    <div className="text-3xl font-black">{v || 0}</div>
    <div className="text-[10px] uppercase font-black opacity-90">{label}</div>
  </div>
);
const Panel = ({ title, children, testid }) => (
  <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-3 space-y-2" data-testid={testid}>
    <div className="text-xs font-black text-purple-300 uppercase">{title}</div>
    <div className="max-h-[420px] overflow-y-auto space-y-1">{children}</div>
  </div>
);
const EmptyLabel = ({ children }) => <div className="text-center opacity-60 py-6 text-[10px]">{children}</div>;

// ============ Sales-by-City Heatmap Widget ============
function CityHeatmap() {
  const [rows, setRows] = useState([]);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [total, setTotal] = useState({ orders: 0, revenue: 0 });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/marketplace/heatmap-cities?days=${days}`);
      setRows(r.data.rows || []);
      setTotal({ orders: r.data.total_orders || 0, revenue: r.data.total_revenue_at || 0 });
    } catch (e) { /* silent — non-critical widget */ }
    finally { setBusy(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const max = rows[0]?.revenue_at || 1;
  const heat = (v) => {
    const pct = Math.min(100, Math.max(4, (v / max) * 100));
    return { width: `${pct}%` };
  };
  const tint = (i) => {
    // top 3 cities → warm, rest → cool
    if (i === 0) return 'bg-gradient-to-r from-rose-500 to-orange-400';
    if (i === 1) return 'bg-gradient-to-r from-orange-400 to-amber-400';
    if (i === 2) return 'bg-gradient-to-r from-amber-400 to-yellow-400';
    return 'bg-gradient-to-r from-indigo-500 to-purple-500';
  };

  return (
    <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-3 space-y-2" data-testid="ops-heatmap-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-black text-purple-300 uppercase">🗺️ Sales Heatmap by City</div>
        <div className="flex items-center gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`heatmap-days-${d}`}
                    className={`px-2 py-0.5 rounded text-[10px] font-black ${days === d ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-300'}`}>
              {d}d
            </button>
          ))}
          <button onClick={load} data-testid="heatmap-refresh" className="p-1 rounded bg-slate-800">
            <RefreshCw size={10} className={busy ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      <div className="text-[10px] opacity-70">
        Last {days} days · <b className="text-emerald-300">{total.orders}</b> orders · <b className="text-yellow-300">{Number(total.revenue).toFixed(0)} AT</b> revenue
      </div>
      <div className="max-h-[300px] overflow-y-auto space-y-1.5">
        {rows.length === 0 && <EmptyLabel>No city data yet</EmptyLabel>}
        {rows.map((r, i) => (
          <div key={r.city} className="text-xs" data-testid={`heatmap-row-${i}`}>
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <div className="font-black flex items-center gap-1.5 min-w-0">
                <span className={`inline-block w-4 text-center font-black ${i < 3 ? 'text-yellow-300' : 'opacity-60'}`}>{i + 1}</span>
                <span className="truncate">{r.city}</span>
              </div>
              <div className="text-right shrink-0">
                <span className="text-yellow-300 font-black">{Number(r.revenue_at).toFixed(0)} AT</span>
                <span className="opacity-70 text-[10px] ml-1">· {r.orders} order{r.orders !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
              <div className={`h-full ${tint(i)} shadow-lg`} style={heat(r.revenue_at)} />
            </div>
            <div className="text-[9px] opacity-60 mt-0.5">
              {r.revenue_share_pct}% of total · {r.delivered} delivered
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ Unified Transaction Ledger ============
const TX_TYPES = [
  { key: '', label: 'All' },
  { key: 'marketplace_order', label: 'Orders' },
  { key: 'marketplace_commission', label: 'Commission' },
  { key: 'marketplace_tax', label: 'Tax' },
  { key: 'marketplace_delivery_payout', label: 'Delivery' },
  { key: 'marketplace_mlm_l1,marketplace_mlm_l2,marketplace_mlm_l3', label: 'MLM Payouts' },
  { key: 'marketplace_platform', label: 'Platform Rev' },
  { key: 'marketplace_auction_win,marketplace_auction_refund,marketplace_bid_hold,marketplace_bid_refund', label: 'Auctions' },
];

function UnifiedLedger() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [wallets, setWallets] = useState([]);
  const [days, setDays] = useState(30);
  const [types, setTypes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set('days', String(days)); params.set('limit', '200');
      if (types) params.set('types', types);
      const r = await api.get(`/admin/marketplace/unified-ledger?${params.toString()}`);
      setRows(r.data.transactions || []); setSummary(r.data.summary_by_type || {}); setWallets(r.data.system_wallets || []);
    } catch { /* silent */ }
    finally { setBusy(false); }
  }, [days, types]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-3 space-y-2" data-testid="ops-ledger-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-black text-purple-300 uppercase">💳 Unified Ledger — AT · Commission · Tax · Delivery · MLM</div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`ledger-days-${d}`}
                    className={`px-2 py-0.5 rounded text-[10px] font-black ${days === d ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-300'}`}>{d}d</button>
          ))}
          <button onClick={load} data-testid="ledger-refresh" className="p-1 rounded bg-slate-800">
            <RefreshCw size={10} className={busy ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {wallets.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="ops-sys-wallets">
          {wallets.map((w) => (
            <div key={w.key || w.name} className="rounded-lg bg-slate-800/60 p-2 text-[10px]" data-testid={`ledger-wallet-${w.key || w.name}`}>
              <div className="opacity-70 uppercase font-black">{w.key || w.name || 'wallet'}</div>
              <div className="text-base font-black text-yellow-300">{Number(w.balance || 0).toFixed(0)}<span className="text-[9px]"> AT</span></div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-1 flex-wrap">
        {TX_TYPES.map((t) => (
          <button key={t.label} onClick={() => setTypes(t.key)} data-testid={`ledger-type-${t.label.replace(/\W/g, '')}`}
                  className={`px-2 py-0.5 rounded text-[10px] font-black ${types === t.key ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-300'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="max-h-[280px] overflow-y-auto space-y-1" data-testid="ledger-rows">
        {rows.length === 0 && <EmptyLabel>No transactions</EmptyLabel>}
        {rows.slice(0, 100).map((tx) => (
          <div key={tx.id} className="flex items-center gap-2 text-[10px] rounded bg-slate-800/40 p-1.5" data-testid={`ledger-tx-${tx.id.slice(0,8)}`}>
            <div className={`w-1.5 h-1.5 rounded-full ${tx.direction === 'credit' ? 'bg-emerald-400' : 'bg-rose-400'}`} />
            <div className="flex-1 min-w-0">
              <div className="font-black truncate">{tx.type} · <span className="opacity-70">{tx.description || '—'}</span></div>
              <div className="opacity-50 text-[9px]">{tx.user_id?.slice(0, 8)} · {(tx.created_at || '').slice(0, 16).replace('T', ' ')}</div>
            </div>
            <div className={`font-black ${tx.direction === 'credit' ? 'text-emerald-300' : 'text-rose-300'}`}>
              {tx.direction === 'credit' ? '+' : '−'}{Number(tx.amount).toFixed(0)} AT
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ============ Delivery Partners Live Panel ============
function DeliveryPartnersPanel() {
  const [partners, setPartners] = useState([]);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [busyIds, setBusyIds] = useState({});

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/delivery/partners?${filter ? `status=${filter}` : ''}`);
      setPartners(r.data.partners || []); setCounts(r.data.counts || {});
    } catch { /* silent */ }
    finally { setBusy(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (id, action) => {
    if (action === 'reject' && !window.confirm('Reject this application?')) return;
    setBusyIds((p) => ({ ...p, [id]: true }));
    try {
      await api.post(`/admin/delivery/partner/${id}/action?action=${action}`, {});
      toast.success(`Partner ${action}d`); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally { setBusyIds((p) => ({ ...p, [id]: false })); }
  };

  return (
    <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-3 space-y-2" data-testid="ops-delivery-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs font-black text-purple-300 uppercase flex items-center gap-1">
          <Bike size={14} /> Delivery Partners
        </div>
        <div className="text-[10px] flex gap-2 opacity-80">
          <span>🟢 Active: <b className="text-emerald-300">{counts.active || 0}</b></span>
          <span>⏳ Pending: <b className="text-amber-300">{counts.pending || 0}</b></span>
          <span>📡 Online: <b className="text-cyan-300">{counts.available || 0}</b></span>
        </div>
      </div>
      <div className="flex gap-1 flex-wrap">
        {[
          { key: 'pending', label: `⏳ Pending (${counts.pending || 0})` },
          { key: 'active', label: `🟢 Active (${counts.active || 0})` },
          { key: '', label: 'All' },
          { key: 'rejected', label: 'Rejected' },
        ].map((f) => (
          <button key={f.label} onClick={() => setFilter(f.key)} data-testid={`delivery-admin-filter-${f.label.replace(/\W/g, '')}`}
                  className={`px-2 py-0.5 rounded text-[10px] font-black ${filter === f.key ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-300'}`}>
            {f.label}
          </button>
        ))}
        <button onClick={load} data-testid="delivery-admin-refresh" className="ml-auto p-1 rounded bg-slate-800">
          <RefreshCw size={10} className={busy ? 'animate-spin' : ''} />
        </button>
      </div>
      <div className="max-h-[240px] overflow-y-auto space-y-1.5" data-testid="delivery-admin-list">
        {partners.length === 0 && <EmptyLabel>No partners in this filter</EmptyLabel>}
        {partners.map((p) => (
          <div key={p.id} className="rounded bg-slate-800/40 p-2 flex items-center gap-2 text-xs" data-testid={`delivery-partner-row-${p.id.slice(0,8)}`}>
            <div className={`w-2 h-2 rounded-full ${p.available && p.status === 'active' ? 'bg-emerald-400 animate-pulse' : p.status === 'active' ? 'bg-slate-500' : p.status === 'pending' ? 'bg-amber-400' : 'bg-rose-400'}`} />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-black truncate">{p.name} · <span className="opacity-70">{p.city}</span> · <span className="uppercase text-[9px] opacity-70">{p.vehicle_type}</span></div>
              <div className="text-[9px] opacity-60">📞 {p.phone} · {p.total_deliveries || 0} deliveries · {Number(p.total_earned_at || 0).toFixed(0)} AT earned</div>
            </div>
            {p.status === 'pending' && (
              <div className="flex gap-1">
                <button onClick={() => act(p.id, 'approve')} disabled={busyIds[p.id]} data-testid={`delivery-approve-${p.id.slice(0,8)}`}
                        className="p-1 rounded bg-emerald-500 text-slate-900 disabled:opacity-40"><CheckCircle size={12} /></button>
                <button onClick={() => act(p.id, 'reject')} disabled={busyIds[p.id]} data-testid={`delivery-reject-${p.id.slice(0,8)}`}
                        className="p-1 rounded bg-rose-600 text-white disabled:opacity-40"><XCircle size={12} /></button>
              </div>
            )}
            {p.status === 'active' && (
              <button onClick={() => act(p.id, 'suspend')} disabled={busyIds[p.id]} data-testid={`delivery-suspend-${p.id.slice(0,8)}`}
                      className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-400/50 text-amber-300 font-black">Suspend</button>
            )}
            {p.status === 'suspended' && (
              <button onClick={() => act(p.id, 'reactivate')} disabled={busyIds[p.id]} data-testid={`delivery-reactivate-${p.id.slice(0,8)}`}
                      className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-400/50 text-emerald-300 font-black">Reactivate</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ============ Voice + Push Alerts (STEP 4) ============
function VoiceAlertsPanel({ counts }) {
  const [enabled, setEnabled] = useState(() => localStorage.getItem('admin_voice_alerts') !== '0');
  const [pushGranted, setPushGranted] = useState(typeof Notification !== 'undefined' && Notification.permission === 'granted');
  const lastCountsRef = useRef({});
  const firstRunRef = useRef(true);

  const totalPending = (counts.pending_payments || 0) + (counts.pending_withdrawals || 0) + (counts.pending_deposits || 0) + (counts.orders_by_status?.pending || 0);

  useEffect(() => { localStorage.setItem('admin_voice_alerts', enabled ? '1' : '0'); }, [enabled]);

  const speakHindi = useCallback((text) => {
    try {
      if (!('speechSynthesis' in window)) return;
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'hi-IN'; u.rate = 1; u.pitch = 1; u.volume = 1;
      const voices = window.speechSynthesis.getVoices();
      const hindi = voices.find((v) => v.lang?.startsWith('hi'));
      if (hindi) u.voice = hindi;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch {}
  }, []);

  const playChime = useCallback((kind = 'order') => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      const patterns = {
        order:    [880, 1174, 1568],
        payment:  [1046, 784, 1046, 1318],
        delivery: [1568, 1318, 1046],
        withdraw: [523, 659, 784],
      };
      const p = patterns[kind] || patterns.order;
      p.forEach((freq, i) => {
        const osc = ctx.createOscillator(); const gain = ctx.createGain();
        osc.type = 'sine'; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now + i * 0.14);
        gain.gain.exponentialRampToValueAtTime(0.3, now + i * 0.14 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.14 + 0.25);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + i * 0.14); osc.stop(now + i * 0.14 + 0.3);
      });
      setTimeout(() => ctx.close(), 1500);
    } catch {}
  }, []);

  const showPush = useCallback((title, body) => {
    try {
      if (!('Notification' in window) || Notification.permission !== 'granted') return;
      const n = new Notification(title, { body, icon: '/favicon.ico' });
      setTimeout(() => { try { n.close(); } catch {} }, 8000);
    } catch {}
  }, []);

  useEffect(() => {
    if (!enabled) { firstRunRef.current = false; return; }
    const cur = {
      payments: counts.pending_payments || 0,
      withdrawals: counts.pending_withdrawals || 0,
      deposits: counts.pending_deposits || 0,
      orders_pending: counts.orders_by_status?.pending || 0,
    };
    const prev = lastCountsRef.current;
    if (!firstRunRef.current) {
      if (cur.orders_pending > (prev.orders_pending || 0)) {
        speakHindi(`Naya order aaya hai. Kul ${cur.orders_pending} pending orders hain.`);
        playChime('order');
        showPush('🛒 New order', `${cur.orders_pending} pending orders`);
      }
      if (cur.payments > (prev.payments || 0) || cur.deposits > (prev.deposits || 0)) {
        speakHindi(`Payment request aayi hai. Approval ke liye dashboard check karein.`);
        playChime('payment');
        showPush('💰 Payment request', `${(cur.payments + cur.deposits)} pending`);
      }
      if (cur.withdrawals > (prev.withdrawals || 0)) {
        speakHindi(`Withdrawal request aayi hai.`);
        playChime('withdraw');
        showPush('⬆️ Withdrawal request', `${cur.withdrawals} pending`);
      }
    }
    firstRunRef.current = false;
    lastCountsRef.current = cur;
  }, [counts, enabled, speakHindi, playChime, showPush]);

  const requestPush = async () => {
    if (!('Notification' in window)) { toast.error('Push not supported'); return; }
    const perm = await Notification.requestPermission();
    setPushGranted(perm === 'granted');
    if (perm === 'granted') { toast.success('🔔 Push notifications enabled'); showPush('✅ Alerts ON', 'You will now receive admin notifications'); }
    else toast.error('Push permission denied');
  };

  const testVoice = () => { speakHindi('Namaste. Ye admin alert test hai. Sab kuch theek se kaam kar raha hai.'); playChime('order'); showPush('🔔 Test alert', 'Voice + chime + push working'); };

  return (
    <div className="rounded-2xl bg-gradient-to-r from-indigo-600/20 to-purple-600/20 border border-indigo-400/40 p-3 flex items-center gap-3 flex-wrap" data-testid="ops-voice-panel">
      <div className="flex-1 min-w-0">
        <div className="text-xs font-black text-indigo-200 flex items-center gap-2">
          {enabled ? <Volume2 size={14} /> : <VolumeX size={14} />} Voice + Push Alerts <span className="text-[10px] opacity-70">(Hindi)</span>
        </div>
        <div className="text-[10px] opacity-80 mt-0.5">
          {enabled ? '🔊 Naye event pe voice + chime + browser push aayenge' : '🔇 Alerts paused'} · <span className="text-amber-300 font-black">{totalPending}</span> total pending items
        </div>
      </div>
      <button onClick={testVoice} data-testid="ops-voice-test"
              className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-[11px] font-black">🎤 Test</button>
      {!pushGranted && (
        <button onClick={requestPush} data-testid="ops-voice-push"
                className="px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-[11px] font-black">🔔 Enable Push</button>
      )}
      <button onClick={() => setEnabled((v) => !v)} data-testid="ops-voice-toggle"
              className={`px-3 py-1.5 rounded-full text-[11px] font-black ${enabled ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-slate-300'}`}>
        {enabled ? '🔊 ON' : '🔇 OFF'}
      </button>
    </div>
  );
}

